import BadgeSelect from "@/components/ui/BadgeSelect";
import { Divider } from "@/components/ui/Divider";
import Dropdown from "@/components/ui/dropdown";
import { IOptionItem } from "@/components/ui/dropdown/types";
import { COUNTRY_DATA } from "@/constants/dataset";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { CountryCode, Source, SpatialAggregationType } from "@/types/common";
import { License } from "@/types/dataset";

import { DataSourceList, DataSourceProvider } from "./DataSourceList";
import { DatasetExplorerSessionStorageData } from ".";

type Props = {
  isUseNewDatasetExplorerModal: boolean;
  countryOptions: { label: string; value: CountryCode }[];
  selectedCountry: { label: string; value: CountryCode | "" };
  setSelectedCountry: (value: {
    label: string;
    value: CountryCode | "";
  }) => void;
  dataSource: Source;
  spatialAgg: SpatialAggregationType | "all";
  license: License;
  selectedDataSources: string[];
  dataSourceProviders?: DataSourceProvider[];
  showCatchmentPopup?: boolean;
  setDataSource: (value: Source) => void;
  setSpatialAgg: (value: SpatialAggregationType | "all") => void;
  setCurrentPage: (value: number) => void;
  setLicense: (value: License) => void;
  setSelectedDataSources: React.Dispatch<React.SetStateAction<string[]>>;
  sessionStorageData: DatasetExplorerSessionStorageData;
  setSessionItem: (data: DatasetExplorerSessionStorageData) => void;
};

const getSpatialAggregationOptions = (
  showCatchmentPopup = false,
  isLinestringEnabled = false
): IOptionItem[] => [
  { label: "All", value: "all", disabled: showCatchmentPopup },
  { label: "Point", value: "poi" },
  { label: "Polygon", value: "polygon", disabled: showCatchmentPopup },
  { label: "H3", value: "H3", disabled: showCatchmentPopup },
  {
    label: "Administrative Area",
    value: "adm_area",
    disabled: showCatchmentPopup
  },
  { label: "Geohash", value: "geohash", disabled: showCatchmentPopup },
  ...(isLinestringEnabled
    ? [{ label: "Line", value: "line", disabled: showCatchmentPopup }]
    : [])
];

const DatasetFilterSection = ({
  isUseNewDatasetExplorerModal,
  countryOptions,
  selectedCountry,
  dataSource,
  spatialAgg,
  license,
  selectedDataSources,
  dataSourceProviders = [],
  setSelectedCountry,
  setDataSource,
  setSpatialAgg,
  setCurrentPage,
  setLicense,
  setSelectedDataSources,
  showCatchmentPopup,
  sessionStorageData,
  setSessionItem
}: Props) => {
  const { isLinestringEnabled } = useAppFeatures();
  const dataSourceBadgeOptions = [
    {
      label: isUseNewDatasetExplorerModal ? "BVT & Partner Data" : "BVT Data",
      value: "bvt",
      disabled: showCatchmentPopup
    },
    { label: "My Organization", value: "user" }
  ];

  const shouldShowDataSourcesList =
    isUseNewDatasetExplorerModal &&
    dataSource === "bvt" &&
    dataSourceProviders.length > 0;

  const shouldShowDivider = !shouldShowDataSourcesList;

  return (
    <div className="overflow-y-auto w-[30%] pl-[1px] h-[calc(100vh-240px)]">
      <Dropdown
        label="Country"
        name="Country"
        data={countryOptions}
        value={selectedCountry}
        onSelect={(data) => {
          setCurrentPage(1);
          setSelectedCountry(data ?? COUNTRY_DATA[0]);
        }}
      />
      <Divider className="bg-gainsboro w-[100%] h-[1px] mt-4" />
      <BadgeSelect
        label="Data Source"
        className="mt-3 text-[12px]"
        showDivider={shouldShowDivider}
        data={dataSourceBadgeOptions}
        itemSelected={dataSource}
        onSelect={(data) => {
          setCurrentPage(1);
          setDataSource(data as Source);
        }}
      />
      {shouldShowDataSourcesList && (
        <DataSourceList
          dataSourceProviders={dataSourceProviders}
          selectedDataSources={selectedDataSources}
          license={license}
          setSelectedDataSources={(value) => {
            setCurrentPage(1);
            setSelectedDataSources(value);
          }}
          setLicense={(data) => {
            setCurrentPage(1);
            setLicense(data);
          }}
          sessionStorageData={sessionStorageData}
          setSessionItem={setSessionItem}
        />
      )}
      <BadgeSelect
        label="Spatial Aggregation"
        className="mt-3"
        showDivider
        data={getSpatialAggregationOptions(
          showCatchmentPopup,
          isLinestringEnabled
        )}
        itemSelected={spatialAgg}
        onSelect={(data) => {
          setCurrentPage(1);
          setSpatialAgg(
            spatialAgg === data && !showCatchmentPopup
              ? "all"
              : (data as SpatialAggregationType)
          );
        }}
      />
    </div>
  );
};

export default DatasetFilterSection;
